import * as React from 'react';
import styles from './index.less';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FInput from '@/components/FInput';
import FResourceCard from '@/components/FResourceCard';
import FLoadingTip from '@/components/FLoadingTip';
import FIcons from '@/components/FIcons';
import fMessage from '@/components/fMessage';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
import * as AHooks from 'ahooks';
import {
  mockCheckPodcastRssUrl,
} from '../mock';
import FPodcastRssSubmitBreadcrumbBar from '../BreadcrumbBar';
import FPodcastRssEpisodeLimitModal, {
  PODCAST_RSS_EPISODE_LIMIT,
} from '../EpisodeLimitModal';
import type {
  FPodcastRssSubmitProps,
  PodcastRssExistingResource,
  PodcastRssPreviewInfo,
  PodcastRssSubmitView,
  PodcastRssVerifiedPayload,
} from '../types';
import {
  isVerificationCodeApiError,
  isVerificationCodeInvalidResponse,
} from '../bindRssFeedError';

type DetectStatus = '' | 'detecting' | 'success' | 'error';

function getErrorMessage(view: PodcastRssSubmitView): string {
  switch (view) {
    case 'error_invalid':
      // 链接无效，请检查后重新输入
      return FI18n.i18nNext.t('submitpodcastwrss_error_invalid');
    case 'error_noemail':
      // RSS feed 缺少所有者电子邮箱
      return FI18n.i18nNext.t('submitpodcastwrss_error_noemail');
    default:
      return '';
  }
}

/** GUID 不匹配数量 = max(N, M) - guidMatchedCount；大于 |M - N| 视为大面积不匹配 */
function isGuidMassMismatch(data: {
  oldFeedItemCount: number;
  newFeedItemCount: number;
  guidMatchedCount: number;
}): boolean {
  const N = Number(data.oldFeedItemCount);
  const M = Number(data.newFeedItemCount);
  const matched = Number(data.guidMatchedCount);
  if (![N, M, matched].every(Number.isFinite)) {
    return false;
  }
  return Math.max(N, M) - matched > Math.abs(M - N);
}

function FPodcastRssSubmit({
  resourceId,
  mode = 'create',
  initialRssUrl = '',
  className = '',
  style,
  onCancel,
  onVerified,
  onCheckRssUrl,
  onSendVerificationCode,
  onStartImport,
}: FPodcastRssSubmitProps) {
  const [$rssUrl, set$rssUrl, get$rssUrl] = FUtil.Hook.useGetState<string>(
    mode === 'update' ? '' : initialRssUrl,
  );
  const [$detectStatus, set$detectStatus] = FUtil.Hook.useGetState<DetectStatus>('');
  const [$detectErrorMessage, set$detectErrorMessage] = FUtil.Hook.useGetState<string>('');
  const [$errorView, set$errorView] = FUtil.Hook.useGetState<PodcastRssSubmitView | ''>('');
  const [$podcastPreview, set$podcastPreview] = FUtil.Hook.useGetState<PodcastRssPreviewInfo | null>(
    null,
  );
  const [$existingResource, set$existingResource] =
    FUtil.Hook.useGetState<PodcastRssExistingResource | null>(null);
  const [$verificationCode, set$verificationCode] = FUtil.Hook.useGetState<string>('');
  const [$verificationError, set$verificationError] = FUtil.Hook.useGetState<string>('');
  const [$sentCaptchaWait, set$sentCaptchaWait, get$sentCaptchaWait] =
    FUtil.Hook.useGetState<number>(0);
  const [$importing, set$importing] = FUtil.Hook.useGetState<boolean>(false);
  const [$isImportingView, set$isImportingView] = FUtil.Hook.useGetState<boolean>(false);
  const [$episodeLimitModalOpen, set$episodeLimitModalOpen] = FUtil.Hook.useGetState<boolean>(false);

  AHooks.useInterval(
    () => {
      set$sentCaptchaWait(get$sentCaptchaWait() - 1);
    },
    $sentCaptchaWait === 0 ? undefined : 1000,
  );

  const isAlreadyExistsError =
    $errorView === 'error_alreadyexists_self' || $errorView === 'error_alreadyexists_other';

  const showVerification =
    $detectStatus === 'success' && $podcastPreview && !$errorView;

  const canStartImport =
    showVerification && $verificationCode.trim() !== '' && !$importing;

  async function handleCheckAddress() {
    const url = get$rssUrl().trim();
    if (!url) {
      set$detectStatus('error');
      // 链接无效，请检查后重新输入
      set$detectErrorMessage(FI18n.i18nNext.t('submitpodcastwrss_error_invalid'));
      return;
    }

    if (mode === 'update' && url === initialRssUrl.trim()) {
      set$detectStatus('error');
      set$detectErrorMessage('新的 RSS 订阅地址不能与原先的地址相同');
      set$errorView('');
      set$podcastPreview(null);
      set$existingResource(null);
      set$verificationCode('');
      set$verificationError('');
      return;
    }

    set$detectStatus('detecting');
    set$detectErrorMessage('');
    set$errorView('');
    set$podcastPreview(null);
    set$existingResource(null);
    set$verificationCode('');
    set$verificationError('');

    try {
      const result = await (onCheckRssUrl || mockCheckPodcastRssUrl)(url);

      if (result.view === 'preview' && result.podcast) {
        set$detectStatus('success');
        set$podcastPreview(result.podcast);
        return;
      }

      // 已占用：检测成功样式 + 播客卡片 + 粉色提示框（对齐 Figma）
      if (
        result.view === 'error_alreadyexists_self' ||
        result.view === 'error_alreadyexists_other'
      ) {
        set$detectStatus('success');
        set$errorView(result.view);
        set$podcastPreview(result.podcast || null);
        set$existingResource(result.existingResource || null);
        return;
      }

      set$detectStatus('error');
      set$errorView(result.view);
      set$detectErrorMessage(getErrorMessage(result.view));
      set$podcastPreview(result.podcast || null);
      set$existingResource(result.existingResource || null);
    } catch {
      set$detectStatus('error');
      set$errorView('error_invalid');
      // 链接无效，请检查后重新输入
      set$detectErrorMessage(FI18n.i18nNext.t('submitpodcastwrss_error_invalid'));
    }
  }

  async function handleSendVerificationCode() {
    if ($sentCaptchaWait > 0) {
      return;
    }
    try {
      let sent = true;
      if (onSendVerificationCode) {
        const result = await onSendVerificationCode(get$rssUrl().trim());
        sent = result !== false;
      }
      if (sent) {
        set$sentCaptchaWait(60);
      }
    } catch {
      // 验证码发送失败
      fMessage('验证码发送失败', 'error');
    }
  }

  async function resolveMatchedItemCount(feedUrl: string): Promise<number | null> {
    const cached = $podcastPreview?.matchedItemCount;
    if (typeof cached === 'number') {
      return cached;
    }
    try {
      const { ret, errCode, data } = await FServiceAPI.Rss.bindingsPreview({
        feedUrl,
        isLoadItemData: 0,
      });
      if (ret !== 0 || errCode !== 0 || !data) {
        return null;
      }
      const count = Number(data.matchedItemCount);
      return Number.isFinite(count) ? count : null;
    } catch {
      return null;
    }
  }

  /** 修改 RSS 时对比新旧 feed；GUID 大面积不匹配则弹确认，用户取消返回 false；验证码错误抛 verification_code_invalid */
  async function confirmGuidMismatchIfNeeded(feedUrl: string): Promise<boolean> {
    if (mode !== 'update' || !resourceId) {
      return true;
    }

    try {
      const { ret, errCode, msg, data } = await FServiceAPI.Rss.bindingsCompare({
        resourceId,
        feedUrl,
        verificationCode: $verificationCode.trim(),
      });
      if (isVerificationCodeInvalidResponse(data, msg)) {
        // 验证码错误
        set$verificationError(FI18n.i18nNext.tAuto('wrong_verified_code'));
        throw new Error('verification_code_invalid');
      }
      if (ret !== 0 || errCode !== 0 || !data) {
        fMessage(msg || 'RSS 对比失败，请稍后重试', 'error');
        return false;
      }

      if (!isGuidMassMismatch(data)) {
        return true;
      }

      // 检测到您的新 RSS 源有大量单集 GUID 不匹配，将作为全新单集发布，是否继续操作？
      const guidChangedMsg = FI18n.i18nNext.t('submitpodcastwrss_msg_guidchanged');
      return fPromiseModalConfirm({
        title: '',
        description:
          guidChangedMsg && guidChangedMsg !== 'submitpodcastwrss_msg_guidchanged'
            ? guidChangedMsg
            : '检测到您的新 RSS 源有大量单集 GUID 不匹配，将作为全新单集发布，是否继续操作？',
        okText:
          // 继续发布
          FI18n.i18nNext.t('submitpodcastwrss_error_alreadyexists_btn_continue'),
        cancelText:
          // 取消
          FI18n.i18nNext.t('btn_cancel'),
      });
    } catch (error) {
      if (error instanceof Error && error.message === 'verification_code_invalid') {
        throw error;
      }
      fMessage('RSS 对比失败，请稍后重试', 'error');
      return false;
    }
  }

  async function runImport(extra?: Pick<PodcastRssVerifiedPayload, 'pubStartDate' | 'pubEndDate'>) {
    const payload: PodcastRssVerifiedPayload = {
      rssUrl: get$rssUrl().trim(),
      verificationCode: $verificationCode.trim(),
      pubStartDate: extra?.pubStartDate,
      pubEndDate: extra?.pubEndDate,
      podcast: $podcastPreview,
    };

    if (mode === 'update' && payload.rssUrl === initialRssUrl.trim()) {
      set$detectStatus('error');
      set$detectErrorMessage('新的 RSS 订阅地址不能与原先的地址相同');
      set$podcastPreview(null);
      throw new Error('rss_url_unchanged');
    }

    const guidOk = await confirmGuidMismatchIfNeeded(payload.rssUrl);
    if (!guidOk) {
      throw new Error('guid_mismatch_cancelled');
    }

    if (!onStartImport && payload.verificationCode === '000000') {
      // 验证码错误
      set$verificationError(FI18n.i18nNext.tAuto('wrong_verified_code'));
      throw new Error('verification_code_invalid');
    }

    if (onStartImport) {
      await onStartImport(payload);
    }
    if (onVerified) {
      await onVerified(payload);
      return;
    }
    set$isImportingView(true);
  }

  function isBenignImportAbort(error: unknown): boolean {
    const message = error instanceof Error ? error.message : '';
    return (
      message === 'rss_url_unchanged' ||
      message === 'guid_mismatch_cancelled' ||
      message === 'verification_code_invalid'
    );
  }

  async function handleStartImport() {
    const feedUrl = get$rssUrl().trim();
    set$importing(true);
    set$verificationError('');
    try {
      const matchedItemCount = await resolveMatchedItemCount(feedUrl);
      if (matchedItemCount == null) {
        fMessage('单集数量校验失败，请稍后重试', 'error');
        return;
      }
      if (matchedItemCount > PODCAST_RSS_EPISODE_LIMIT) {
        set$episodeLimitModalOpen(true);
        return;
      }
      await runImport();
    } catch (error) {
      if (isBenignImportAbort(error)) {
        return;
      }
      // 验证码错误：仅输入框下方提示；其它错误：仅顶部 toast
      if (isVerificationCodeApiError(error)) {
        set$verificationError(
          error instanceof Error && error.message.trim()
            ? error.message
            : // 验证码错误
              FI18n.i18nNext.tAuto('wrong_verified_code'),
        );
        return;
      }
      const backendMsg = error instanceof Error ? error.message.trim() : '';
      if (backendMsg) {
        fMessage(backendMsg, 'error');
      } else {
        // 验证码错误
        set$verificationError(FI18n.i18nNext.tAuto('wrong_verified_code'));
      }
    } finally {
      set$importing(false);
    }
  }

  async function handleEpisodeLimitStartImport(dates: {
    pubStartDate: string;
    pubEndDate: string;
  }) {
    set$importing(true);
    set$verificationError('');
    try {
      await runImport(dates);
      set$episodeLimitModalOpen(false);
    } catch (error) {
      if (isBenignImportAbort(error)) {
        return;
      }
      if (isVerificationCodeApiError(error)) {
        set$verificationError(
          error instanceof Error && error.message.trim()
            ? error.message
            : // 验证码错误
              FI18n.i18nNext.tAuto('wrong_verified_code'),
        );
        return;
      }
      const backendMsg = error instanceof Error ? error.message.trim() : '';
      if (backendMsg) {
        fMessage(backendMsg, 'error');
      } else {
        // 验证码错误
        set$verificationError(FI18n.i18nNext.tAuto('wrong_verified_code'));
      }
    } finally {
      set$importing(false);
    }
  }

  function renderExistingResourceCard(resource: PodcastRssExistingResource) {
    return (
      <div
        className={styles.existingResourceCard}
        onClick={() => {
          window.open(
            FUtil.LinkTo.collectionDetails({
              collectionID: resource.id,
            }),
            '_blank',
          );
        }}
      >
        <FResourceCard type='market' resource={resource} />
      </div>
    );
  }

  function handleContinuePublish() {
    set$detectStatus('success');
    set$errorView('');
    set$detectErrorMessage('');
    if (!$podcastPreview && $existingResource) {
      set$podcastPreview({
        title: $existingResource.title,
        cover: $existingResource.cover,
        author: $existingResource.username,
        episodeCount: $existingResource.collection?.count || 0,
        maskedEmail: '_pod***@gmail.com',
      });
    }
  }

  function renderAlreadyExistsBox() {
    if (!isAlreadyExistsError) {
      return null;
    }

    return (
      <div className={styles.duplicateBox}>
        <div className={styles.duplicateHeader}>
          <span className={styles.duplicateError}>
            {$errorView === 'error_alreadyexists_self'
              ? // 您已提交了此播客
                FI18n.i18nNext.tAuto('submitpodcastwrss_error_alreadyexists')
              : // 此播客已存在
                FI18n.i18nNext.tAuto('submitpodcastwrss_error_alreadyexists02')}
          </span>
          {$errorView === 'error_alreadyexists_self' && (
            <button
              type='button'
              className={styles.duplicateContinue}
              onClick={handleContinuePublish}
            >
              {
                // 继续发布
                FI18n.i18nNext.tAuto('submitpodcastwrss_error_alreadyexists_btn_continue')
              }
            </button>
          )}
        </div>
        {$existingResource && renderExistingResourceCard($existingResource)}
      </div>
    );
  }

  function renderPodcastCard(podcast: PodcastRssPreviewInfo) {
    const defaultCover = `${FUtil.Domain.STATIC_ORIGIN}/static/default_cover.png`;

    return (
      <div className={styles.podcastCard}>
        <div className={styles.cover}>
          <img
            src={podcast.cover || defaultCover}
            alt=''
            onError={(e) => {
              (e.target as HTMLImageElement).src = defaultCover;
            }}
          />
        </div>
        <div className={styles.meta}>
          <div className={styles.podcastTitle}>{podcast.title}</div>
          <div className={styles.podcastMeta}>主播：{podcast.author}</div>
          <div className={styles.podcastMeta}>单集数量：{podcast.episodeCount}</div>
        </div>
      </div>
    );
  }

  const rootClassName = [styles.root, className].filter(Boolean).join(' ');

  if ($isImportingView) {
    return (
      <div className={rootClassName} style={style}>
        <div className={styles.processingBox}>
          <FLoadingTip height={120} />
          <div style={{ height: 16 }} />
          <FComponentsLib.FContentText
            text={
              // 正在导入您的播客信息
              FI18n.i18nNext.tAuto('submitpodcastwrss_msg_processing')
            }
            type='additional2'
          />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName} style={style}>
      {mode === 'update' ? (
        <FComponentsLib.FTitleText type='h2' text='修改RSS订阅地址' />
      ) : (
        <FPodcastRssSubmitBreadcrumbBar activeLabel='RSS订阅' onReselect={onCancel} />
      )}

      <div className={styles.sections}>
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>RSS 订阅地址</div>
          <div className={styles.inputSection}>
            <div className={styles.inputRow}>
              <FInput
                wrapClassName={[
                  styles.urlInputWrap,
                  $detectStatus === 'error' && $detectErrorMessage !== ''
                    ? styles.urlInputError
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                value={$rssUrl}
                placeholder='https://'
                onChange={(e) => {
                  set$rssUrl(e.target.value);
                  if ($detectStatus !== '') {
                    set$detectStatus('');
                    set$detectErrorMessage('');
                    set$errorView('');
                    set$podcastPreview(null);
                  }
                }}
                onPressEnter={() => {
                  if ($detectStatus !== 'detecting') {
                    handleCheckAddress();
                  }
                }}
              />
              <button
                type='button'
                className={styles.outlineBtn}
                disabled={$detectStatus === 'detecting' || get$rssUrl().trim() === ''}
                onClick={handleCheckAddress}
              >
                检测地址
              </button>
            </div>
            {$detectStatus === 'detecting' && (
              <div className={[styles.statusRow, styles.statusDetecting].join(' ')}>
                <FIcons.FLoading style={{ fontSize: 14, color: '#2784FF' }} />
                <span>检测中</span>
              </div>
            )}
            {$detectStatus === 'success' && (
              <div className={[styles.statusRow, styles.statusSuccess].join(' ')}>
                <FIcons.FCheck style={{ fontSize: 14, color: '#44C28C' }} />
                <span>检测成功</span>
              </div>
            )}
            {$detectStatus === 'error' && $detectErrorMessage !== '' && (
              <div className={[styles.statusRow, styles.statusError].join(' ')}>
                {$detectErrorMessage}
              </div>
            )}
          </div>

          {$detectStatus === 'success' && $podcastPreview && renderPodcastCard($podcastPreview)}
          {renderAlreadyExistsBox()}
        </div>

        {showVerification && $podcastPreview && (
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>验证播客所有权</div>
            <div className={styles.sectionSubtitle}>
              我们检测到该 RSS 绑定的邮箱为 {$podcastPreview.maskedEmail || '_pod***@gmail.com'}
              。请输入发送到该邮箱的 6 位验证码。
            </div>
            <div
              className={[
                styles.inputSection,
                $verificationError !== '' ? styles.inputSectionWithError : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className={styles.verifyRow}>
                <FInput
                  className={styles.verifyInput}
                  wrapClassName={[
                    styles.verifyInput,
                    $verificationError !== '' ? styles.verifyInputError : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  value={$verificationCode}
                  placeholder='请输入验证码'
                  errorText={
                    $verificationError ? (
                      <span className={styles.verifyErrorText}>{$verificationError}</span>
                    ) : undefined
                  }
                  onChange={(e) => {
                    set$verificationCode(e.target.value);
                    set$verificationError('');
                  }}
                />
                <button
                  type='button'
                  className={styles.outlineBtn}
                  disabled={$sentCaptchaWait > 0}
                  onClick={handleSendVerificationCode}
                >
                  {$sentCaptchaWait === 0
                    ? // 获取验证码
                      FI18n.i18nNext.tAuto('flw_signup_btn_sendcode')
                    : // 获取验证码倒计时
                      FI18n.i18nNext.tAuto('flw_verify_btn_sendcode_timer', {
                        timer: $sentCaptchaWait,
                      })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {mode === 'update' ? (
          <>
            <FComponentsLib.FRectBtn
              type='transparent'
              className={styles.footerCancelBtn}
              onClick={onCancel}
            >
              取消修改
            </FComponentsLib.FRectBtn>
            <FComponentsLib.FRectBtn
              type='primary'
              disabled={!canStartImport}
              loading={$importing && !$episodeLimitModalOpen}
              onClick={handleStartImport}
            >
              确认修改
            </FComponentsLib.FRectBtn>
          </>
        ) : (
          <FComponentsLib.FRectBtn
            type='primary'
            disabled={!canStartImport}
            loading={$importing && !$episodeLimitModalOpen}
            onClick={handleStartImport}
          >
            开始导入
          </FComponentsLib.FRectBtn>
        )}
      </div>

      <FPodcastRssEpisodeLimitModal
        open={$episodeLimitModalOpen}
        feedUrl={get$rssUrl().trim()}
        importing={$importing}
        onCancel={() => {
          if (!$importing) {
            set$episodeLimitModalOpen(false);
          }
        }}
        onStartImport={handleEpisodeLimitStartImport}
      />
    </div>
  );
}

export default FPodcastRssSubmit;
